import { Stack, Text } from "@chakra-ui/react";
import { ListWithLabel } from "@/components/list";
import { TEXT_VARIANTS } from "@/theme/components/text";

const DisplayList = ({
  text,
  points,
  concText,
}: {
  text?: string;
  points: { label: string; text: string }[];
  concText?: string;
}) => {
  return (
    <Stack spacing={3}>
      <Text variant={TEXT_VARIANTS.BODY2} color="text.100">
        {text}
      </Text>
      <ListWithLabel points={points} />
      {concText && (
        <Text variant={TEXT_VARIANTS.BODY2} color="text.100">
          {concText}
        </Text>
      )}
    </Stack>
  );
};

export const FAQS = [
  {
    title: "What types of applications can I deploy on Virtuozzo PaaS?",
    content:
      "Virtuozzo PaaS supports a wide range of applications, including web applications, databases, and custom applications across various programming languages.",
  },
  {
    title: "How does automatic scaling work in Virtuozzo PaaS?",
    content:
      "The platform monitors resource usage and automatically adjusts CPU and RAM allocation based on the application's real-time needs, ensuring optimal performance and cost savings.",
  },
  {
    title: "Is there support for containerized applications?",
    content: (
      <DisplayList
        text="Yes. Virtuozzo PaaS fully supports Docker containers and Kubernetes-based applications."
        points={[
          {
            label: "",
            text: "Developers can deploy containerized microservices with auto-scaling and built-in networking.",
          },
          {
            label: "",
            text: "The platform provides native support for orchestration, ensuring seamless CI/CD integration.",
          },
        ]}
      />
    ),
  },
  {
    title: "How does Virtuozzo PaaS ensure high availability?",
    content: (
      <DisplayList
        points={[
          {
            label: "Automatic Failover",
            text: "If an instance fails, workloads are automatically redistributed.",
          },
          {
            label: "Load Balancing",
            text: "Distributes traffic evenly across multiple containers or application nodes.",
          },
          {
            label: "Clustered Deployments",
            text: "Supports high-availability clustering for databases and applications.",
          },
        ]}
      />
    ),
  },
  {
    title: "Can I integrate DevOps tools with Virtuozzo PaaS?",
    content: (
      <DisplayList
        text="Yes. The platform supports Jenkins, GitLab CI/CD, and other DevOps tools"
        points={[
          {
            label: "",
            text: "Developers can automate builds, testing, and deployments using Git-based workflows.",
          },
          {
            label: "",
            text: "Pre-configured CI/CD pipelines are available for streamlined continuous deployment.",
          },
        ]}
      />
    ),
  },
  {
    title: "What are the security features in Virtuozzo PaaS?",
    content: (
      <DisplayList
        points={[
          {
            label: "Role-Based Access Control (RBAC)",
            text: "Ensures that users have appropriate permissions.",
          },
          {
            label: "Data Encryption",
            text: "Supports TLS encryption for in-transit and at-rest data security.",
          },
          {
            label: "Firewalls & DDoS Protection",
            text: "Built-in network security policies protect against attacks.",
          },
          {
            label: "Automated Security Patching",
            text: "Ensures applications stay up to date with the latest patches.",
          },
        ]}
      />
    ),
  },
  {
    title: "Does Virtuozzo PaaS support multi-cloud environments?",
    content: (
      <DisplayList
        text={
          "Yes. The platform allows workloads to run across public, private, and hybrid clouds."
        }
        points={[
          {
            label: "",
            text: "Developers can deploy applications on AWS, Azure, Google Cloud, and private infrastructure",
          },
          {
            label: "",
            text: "It provides centralized management for multi-cloud workloads. ",
          },
        ]}
      />
    ),
  },
  {
    title: "How does Virtuozzo PaaS help reduce costs?",
    content: (
      <DisplayList
        points={[
          {
            label: "Pay-As-You-Go Model",
            text: "Charges only for the actual resource consumption.",
          },
          {
            label: "Automated Scaling",
            text: "Prevents over-provisioning with vertical and horizontal scaling.",
          },
          {
            label: "Efficient Storage Management",
            text: "Reduces storage costs with optimized block and object storage.",
          },
        ]}
      />
    ),
  },
  {
    title: "Can I deploy databases on Virtuozzo PaaS? ",
    content: (
      <DisplayList
        text={
          "Yes. The platform supports databases like MySQL, PostgreSQL, MongoDB, Redis, and MariaDB. "
        }
        points={[
          {
            label: " Clustering & Replication",
            text: "Enables automatic failover and high availability.",
          },
          {
            label: "Database-as-a-Service (DBaaS)",
            text: "Simplifies database management through automation. ",
          },
        ]}
      />
    ),
  },
  {
    title: "What is the Virtuozzo PaaS Marketplace?  ",
    content: (
      <DisplayList
        text={
          "A pre-packaged app store offering one-click deployment for applications like:  "
        }
        points={[
          {
            label: "WordPress, Magento, Joomla, Drupal",
            text: "CMS platforms.",
          },
          {
            label: "NGINX, Apache, Tomcat, GlassFish, WildFly ",
            text: "Web server environments. ",
          },
          {
            label: "Kubernetes, Docker Swarm",
            text: "Container orchestration tools. ",
          },
          {
            label: "Elasticsearch, RabbitMQ, Kafka",
            text: "Data processing solutions. ",
          },
        ]}
        concText="Reduces deployment time with pre-configured infrastructure templates. "
      />
    ),
  },
  {
    title: "Can I migrate existing applications to Virtuozzo PaaS?",
    content: (
      <DisplayList
        text={"Yes. The platform supports:"}
        points={[
          {
            label: "",
            text: "Lift-and-shift migrations of existing applications. ",
          },
          {
            label: "",
            text: "Cloud-native re-architecting for better scalability. ",
          },
          {
            label: "",
            text: "Automated import tools for seamless migration. ",
          },
        ]}
      />
    ),
  },
  {
    title: "How does Virtuozzo PaaS handle backups and disaster recovery?",
    content: (
      <DisplayList
        points={[
          {
            label: "Automated Backups",
            text: "Users can configure scheduled or on-demand backups to prevent data loss.",
          },
          {
            label: "Snapshot-Based Recovery",
            text: "Quickly restore applications to previous states using snapshot backups.",
          },
          {
            label: "Geo-Redundant Replication",
            text: "Ensures high availability by replicating data across multiple regions.",
          },
        ]}
      />
    ),
  },
];
